import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader } from "@/crm/ui";
import { toast } from "sonner";
import {
  Sliders, GitBranch, Tag, RefreshCw, X, Plus, Sparkles, Mail, MapPin, Radar,
  CheckCircle2, XCircle, GripVertical, RotateCcw, MonitorPlay, Link2, KeyRound, Bot,
} from "lucide-react";
import { useAuth } from "@/context/CrmAuth";
import Modal from "@/crm/components/Modal";

const SECRET_MASK = "********";

const RULE_LABELS = {
  secteur_correspondant: "Secteur correspondant", telephone_present: "Téléphone disponible",
  email_present: "Email disponible", site_web_present: "Site web actif",
  entreprise_active: "Entreprise active", effectif_interessant: "Effectif intéressant",
  code_postal_cible: "Code postal ciblé",
};

const RULE_HINTS = {
  secteur_correspondant: "Le code NAF cible la liste",
  telephone_present: "Numéro direct vérifié",
  email_present: "Adresse professionnelle trouvée",
  site_web_present: "Domaine résolu et en ligne",
  entreprise_active: "Non radiée au registre",
  effectif_interessant: "Tranche de salariés ciblée",
  code_postal_cible: "Dans la zone de prospection",
};

// Réutilise la palette des statuts de pipeline (voir STATUS_COLORS dans crm/ui.jsx)
// pour que l'ordre des étapes ici corresponde visuellement au tableau Pipeline.
const STAGE_DOTS = [
  "bg-slate-400", "bg-violet-500", "bg-blue-500", "bg-cyan-500", "bg-indigo-500",
  "bg-amber-500", "bg-orange-500", "bg-fuchsia-500", "bg-emerald-500", "bg-rose-500", "bg-yellow-500",
];

const TABS = [
  { id: "scoring", label: "Scoring", icon: Sliders },
  { id: "pipeline", label: "Pipeline", icon: GitBranch },
  { id: "tags", label: "Tags", icon: Tag },
  { id: "prospection", label: "Prospection", icon: MonitorPlay },
  { id: "enrichissement", label: "Enrichissement", icon: Radar },
  { id: "integrations", label: "Intégrations", icon: Sparkles },
];

// Onglets sans formulaire : la barre d'enregistrement n'a rien à y faire.
const READONLY_TABS = ["enrichissement"];

// Fiabilité affichée : la couleur dit d'un coup d'œil si la source fait
// autorité sur ce champ ou si elle ne fait que combler un vide.
const TRUST_STYLE = {
  "très forte": "bg-emerald-50 text-emerald-700",
  forte: "bg-sky-50 text-sky-700",
  moyenne: "bg-amber-50 text-amber-700",
  faible: "bg-slate-100 text-slate-500",
};

const HEALTH_STYLE = {
  ok: "bg-emerald-500", degraded: "bg-amber-500",
  down: "bg-rose-500", disabled: "bg-slate-300",
};

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState({});
  const [stages, setStages] = useState([]);
  const [tags, setTags] = useState([]);
  const [newStage, setNewStage] = useState("");
  const [newTag, setNewTag] = useState("");
  // Lien de la vidéo de démo envoyée depuis les fiches (« Planifier une action »).
  const [demoVideoUrl, setDemoVideoUrl] = useState("");

  const [integrations, setIntegrations] = useState(null);
  const [savingIntegrations, setSavingIntegrations] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});

  // Catalogue de l'enrichissement : sources interrogées et enchaînement des
  // deux moteurs. Servi par le backend (crm/enrichment/catalog.py) pour que
  // l'écran décrive ce qui tourne réellement, sans le recopier ici.
  const [catalog, setCatalog] = useState(null);
  const [healths, setHealths] = useState(null);
  const [checking, setChecking] = useState(false);

  const [tab, setTab] = useState("scoring");
  // `dirty` par section : la barre d'enregistrement n'agit que sur l'onglet actif.
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [rescoreOpen, setRescoreOpen] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [drag, setDrag] = useState(null);

  const load = () => api.get("/settings").then(({ data }) => {
    setSettings(data); setRules(data.scoring_rules); setStages(data.pipeline_stages); setTags(data.tags);
    setDemoVideoUrl(data.demo_video_url || "");
    setDirty((d) => ({ ...d, scoring: false, pipeline: false, tags: false, prospection: false }));
  }).catch(() => {});
  const loadIntegrations = () => api.get("/settings/integrations").then(({ data }) => {
    setIntegrations(data); setDirty((d) => ({ ...d, integrations: false }));
  }).catch(() => {});
  useEffect(() => { load(); loadIntegrations(); }, []);

  // Chargé à la première visite de l'onglet seulement : inutile de décrire le
  // moteur à qui vient régler son scoring.
  useEffect(() => {
    if (tab !== "enrichissement" || catalog) return;
    api.get("/enrichment/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, [tab, catalog]);

  // Sonde réseau de chaque source — à la demande, jamais au chargement.
  const checkSources = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/enrichment/providers/health");
      setHealths(Object.fromEntries(data.providers.map((p) => [p.name, p])));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Vérification impossible"); }
    finally { setChecking(false); }
  };

  const touch = (section) => setDirty((d) => ({ ...d, [section]: true }));

  const saveIntegrations = async () => {
    setSavingIntegrations(true);
    try {
      const { data } = await api.put("/settings/integrations", integrations);
      setIntegrations(data);
      setDirty((d) => ({ ...d, integrations: false }));
      toast.success("Intégrations enregistrées");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement"); }
    finally { setSavingIntegrations(false); }
  };

  const testIntegration = async (key) => {
    setTesting(key);
    try {
      const { data } = await api.post(`/settings/integrations/test-${key}`);
      setTestResults((r) => ({ ...r, [key]: data }));
      if (data.ok) toast.success(key === "gemini" ? "Clé Gemini valide" : "Clé Resend valide");
      else toast.error(data.error || "Échec du test");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec du test"); }
    finally { setTesting(null); }
  };

  // Une seule barre d'enregistrement : on n'envoie que la section affichée.
  const save = async () => {
    if (tab === "integrations") return saveIntegrations();
    setSaving(true);
    try {
      if (tab === "scoring") { await api.put("/settings/scoring", { rules }); toast.success("Règles de scoring enregistrées"); }
      if (tab === "pipeline") { await api.put("/settings/pipeline", { stages }); toast.success("Pipeline enregistré"); }
      if (tab === "tags") { await api.put("/settings/tags", { tags }); toast.success("Tags enregistrés"); }
      if (tab === "prospection") {
        await api.put("/settings/prospection", { demo_video_url: demoVideoUrl.trim() });
        toast.success("Réglages de prospection enregistrés");
      }
      setDirty((d) => ({ ...d, [tab]: false }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const rescoreAll = async () => {
    setRescoring(true);
    try {
      const { data } = await api.post("/settings/rescore-all");
      toast.success(`${data.rescored} entreprises recalculées`);
      setRescoreOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec du recalcul"); }
    finally { setRescoring(false); }
  };

  const setRule = (key, value) => {
    const v = Math.max(0, Math.min(60, Number(value) || 0));
    setRules((r) => ({ ...r, [key]: v }));
    touch("scoring");
  };

  const total = useMemo(
    () => Object.values(rules).reduce((a, v) => a + (Number(v) || 0), 0),
    [rules]
  );

  const onDrop = (i) => {
    if (drag === null || drag === i) return setDrag(null);
    setStages((st) => { const c = [...st]; const [m] = c.splice(drag, 1); c.splice(i, 0, m); return c; });
    setDrag(null); touch("pipeline");
  };

  if (!settings) return <Loader />;

  const isDirty = !!dirty[tab];
  const canSave = tab === "integrations" ? isAdmin && isDirty : isDirty;
  const busy = saving || savingIntegrations;

  return (
    <div>
      <PageHeader
        title="Paramètres"
        subtitle="Scoring, pipeline, tags, prospection, sources d’enrichissement et intégrations de l’espace. Les changements s’appliquent à toute l’équipe."
      />

      <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)] items-start">
        {/* Onglets : rail vertical en desktop, barre défilante en mobile */}
        <div className="bg-white rounded-xl border border-slate-200 p-2 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar lg:sticky lg:top-20">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              aria-current={tab === t.id}
              className={cnTab(tab === t.id)}
            >
              <t.icon size={16} className="shrink-0" />
              {t.label}
              {dirty[t.id] && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Modifications non enregistrées" />}
            </button>
          ))}
        </div>

        <div className="min-w-0 space-y-5">
          {tab === "scoring" && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div>
                    <h3 className="font-heading font-semibold text-slate-800">Règles de scoring</h3>
                    <p className="text-sm text-slate-500 mt-1">Poids attribué à chaque signal lors du calcul du score.</p>
                  </div>
                  <button
                    onClick={() => { setRules(settings.scoring_rules); setDirty((d) => ({ ...d, scoring: false })); }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
                  >
                    <RotateCcw size={13} /> Rétablir
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {Object.entries(rules).map(([key, val]) => (
                    <div
                      key={key}
                      className="grid grid-cols-[minmax(0,1fr)_96px] sm:grid-cols-[minmax(140px,1.2fr)_minmax(90px,1fr)_96px] gap-x-4 gap-y-3 items-center py-3.5"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700">{RULE_LABELS[key] || key}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{RULE_HINTS[key]}</div>
                      </div>
                      {/* Le curseur passe sur sa propre ligne en mobile */}
                      <input
                        type="range" min={0} max={30} step={5} value={val}
                        onChange={(e) => setRule(key, e.target.value)}
                        aria-label={RULE_LABELS[key] || key}
                        className="col-span-2 sm:col-span-1 order-last sm:order-none w-full min-w-0 accent-indigo-600"
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <input
                          type="number" value={val} data-testid={`rule-${key}`}
                          onChange={(e) => setRule(key, e.target.value)}
                          className="w-16 px-2 py-1.5 rounded-lg border border-slate-300 text-sm text-center font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                        />
                        <span className="text-xs text-slate-400">pts</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Synthèse du barème : 2 colonnes fluides, empilée en mobile */}
              <div className="bg-slate-900 text-slate-200 rounded-xl p-5 grid gap-5 sm:grid-cols-[minmax(200px,260px)_minmax(0,1fr)] sm:items-center">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Score maximum</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-4xl font-heading font-bold text-white tracking-tight font-mono" data-testid="scoring-total">{total}</span>
                    <span className="text-sm text-slate-500">/ 100 pts</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-4 mb-2">
                    <div className="h-full rounded-full bg-indigo-500 transition-[width] duration-200" style={{ width: `${Math.min(100, total)}%` }} />
                  </div>
                  <p className="text-xs text-slate-400">
                    {total === 100 ? "Barème équilibré sur 100 points."
                      : total > 100 ? "Le barème dépasse 100 : les scores seront plafonnés."
                      : `Il reste ${100 - total} points à répartir.`}
                  </p>
                </div>
                <div className="min-w-0 sm:border-l sm:border-white/10 sm:pl-5">
                  <button
                    data-testid="rescore-all" onClick={() => setRescoreOpen(true)}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 py-2.5 text-sm font-medium text-white transition-colors"
                  >
                    <RefreshCw size={15} /> Recalculer tout
                  </button>
                  <p className="text-xs text-slate-500 mt-2.5">
                    Applique le barème <b className="font-semibold text-slate-400">enregistré</b> à toutes les entreprises.
                  </p>
                </div>
              </div>
            </>
          )}

          {tab === "pipeline" && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800">Étapes du pipeline</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">Glissez pour réordonner. Chaque étape devient une colonne du pipeline.</p>
              <div className="space-y-2">
                {stages.map((s, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => setDrag(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(i)}
                    onDragEnd={() => setDrag(null)}
                    data-testid={`stage-row-${i}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 bg-slate-50/60 transition-colors ${drag === i ? "border-indigo-400 opacity-60" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <GripVertical size={15} className="text-slate-300 shrink-0 cursor-grab" />
                    <span className={`h-2 w-2 rounded-full shrink-0 ${STAGE_DOTS[i % STAGE_DOTS.length]}`} />
                    <input
                      value={s}
                      onChange={(e) => { setStages((st) => st.map((x, j) => (j === i ? e.target.value : x))); touch("pipeline"); }}
                      className="flex-1 min-w-0 bg-transparent text-sm font-medium text-slate-700 outline-none"
                    />
                    <button
                      onClick={() => { setStages((st) => st.filter((_, j) => j !== i)); touch("pipeline"); }}
                      aria-label={`Supprimer ${s}`}
                      className="shrink-0 text-slate-300 hover:text-rose-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <input
                  value={newStage}
                  onChange={(e) => setNewStage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && newStage.trim()) { setStages((st) => [...st, newStage.trim()]); setNewStage(""); touch("pipeline"); } }}
                  placeholder="Nouvelle étape…"
                  className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm focus:border-solid focus:border-indigo-500 outline-none"
                />
                <button
                  onClick={() => { if (newStage.trim()) { setStages((st) => [...st, newStage.trim()]); setNewStage(""); touch("pipeline"); } }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                >
                  <Plus size={15} /> Ajouter
                </button>
              </div>
            </div>
          )}

          {tab === "tags" && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800">Tags personnalisés</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">Utilisés pour segmenter les listes et filtrer les entreprises.</p>
              <div className="flex flex-wrap gap-2">
                {tags.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 pl-3 pr-2 py-1.5 text-sm text-slate-700">
                    {t}
                    <button
                      onClick={() => { setTags((tg) => tg.filter((_, j) => j !== i)); touch("tags"); }}
                      aria-label={`Supprimer ${t}`}
                      className="text-slate-300 hover:text-slate-600 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
                {tags.length === 0 && <p className="text-sm text-slate-400">Aucun tag pour le moment.</p>}
              </div>
              <div className="flex gap-2 mt-4 max-w-md">
                <input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && newTag.trim()) { setTags((tg) => [...tg, newTag.trim().toUpperCase()]); setNewTag(""); touch("tags"); } }}
                  placeholder="Nouveau tag…"
                  className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm focus:border-solid focus:border-indigo-500 outline-none"
                />
                <button
                  onClick={() => { if (newTag.trim()) { setTags((tg) => [...tg, newTag.trim().toUpperCase()]); setNewTag(""); touch("tags"); } }}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                >
                  Ajouter
                </button>
              </div>
            </div>
          )}

          {tab === "prospection" && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800">Vidéo de démonstration</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Lien proposé par défaut quand un commercial envoie la vidéo de démo depuis une fiche
                (« Planifier une action » → « Vidéo de démo »). Il reste modifiable au cas par cas.
              </p>
              <label className="block max-w-xl">
                <span className="block font-mono text-[10.5px] tracking-[.1em] uppercase text-slate-400 mb-1">Lien de la vidéo</span>
                <div className="relative">
                  <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="settings-demo-video-url"
                    value={demoVideoUrl}
                    onChange={(e) => { setDemoVideoUrl(e.target.value); touch("prospection"); }}
                    placeholder="https://www.youtube.com/watch?v=…"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-500"
                  />
                </div>
              </label>
            </div>
          )}

          {/* Enrichissement — où le CRM cherche, et dans quel ordre.
              Lecture seule : tout vient de /enrichment/catalog, qui décrit le
              moteur réellement exécuté (sources, fiabilités, étapes). */}
          {tab === "enrichissement" && (
            catalog ? (
              <>
                {/* Les deux moteurs, étape par étape */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-heading font-semibold text-slate-800">Les deux modes d’enrichissement</h3>
                  <p className="text-sm text-slate-500 mt-1 mb-5">
                    Ce que fait le CRM, dans l’ordre, quand vous lancez un enrichissement
                    depuis une fiche ou depuis l’écran Enrichissement.
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {catalog.pipelines.map((p) => (
                      <div key={p.id} data-testid={`pipeline-${p.id}`}
                        className={`rounded-xl border p-4 ${p.id === "ai" ? "border-violet-200 bg-violet-50/30" : "border-slate-200"}`}>
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          {p.id === "ai" ? <Bot size={15} className="text-violet-500" /> : <Sparkles size={15} className="text-indigo-500" />}
                          {p.label}
                        </div>
                        <p className="text-xs text-slate-500 mt-1.5 mb-3">{p.resume}</p>
                        <ol className="space-y-2.5">
                          {p.steps.map((s, i) => (
                            <li key={s.titre} className="flex gap-2.5">
                              <span className={`shrink-0 h-5 w-5 rounded-full text-[11px] font-semibold flex items-center justify-center ${p.id === "ai" ? "bg-violet-100 text-violet-700" : "bg-indigo-50 text-indigo-600"}`}>
                                {i + 1}
                              </span>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-700">{s.titre}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{s.detail}</div>
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sources interrogées, dans l'ordre réel d'interrogation */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                    <h3 className="font-heading font-semibold text-slate-800">Sources interrogées</h3>
                    <button data-testid="check-sources" onClick={checkSources} disabled={checking}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                      <RefreshCw size={13} className={checking ? "animate-spin" : ""} /> Vérifier les sources
                    </button>
                  </div>
                  <p className="text-sm text-slate-500 mt-1 mb-5">
                    Dans leur ordre d’interrogation : les sources qui découvrent un site web
                    passent avant celles qui le lisent. Chaque source ne fait autorité que sur
                    certains champs — c’est ce qui départage deux informations contradictoires.
                  </p>
                  <div className="space-y-3">
                    {catalog.providers.map((p, i) => {
                      const h = healths?.[p.name];
                      return (
                        <div key={p.name} data-testid={`source-${p.name}`} className="rounded-xl border border-slate-200 p-4">
                          <div className="flex items-start gap-3">
                            <span className="shrink-0 mt-0.5 h-6 w-6 rounded-lg bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center">
                              {i + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-slate-800">{p.label}</span>
                                {/* La famille de source n'est utile que si elle
                                    apprend quelque chose : « Registre public »
                                    deux fois de suite ne dit rien de plus. */}
                                {p.source_type_label !== p.label && (
                                  <span className="text-xs text-slate-400">{p.source_type_label}</span>
                                )}
                                {p.requires_key && (
                                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-amber-50 text-amber-700">
                                    <KeyRound size={11} /> clé requise
                                  </span>
                                )}
                                {h && (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                                    <span className={`h-1.5 w-1.5 rounded-full ${HEALTH_STYLE[h.status] || "bg-slate-300"}`} />
                                    {/* Latence affichée seulement si une requête
                                        a réellement eu lieu : une source qui lit
                                        le site de la fiche ne sonde rien (0 ms). */}
                                    {h.status === "ok" ? `disponible${h.latency_ms ? ` (${h.latency_ms} ms)` : ""}` : (h.detail || h.status)}
                                  </span>
                                )}
                              </div>
                              {p.note && <p className="text-xs text-slate-500 mt-1.5">{p.note}</p>}
                              <div className="flex flex-wrap gap-1.5 mt-2.5">
                                {p.fields.map((f) => (
                                  <span key={f.field} title={`Fiabilité ${f.niveau} sur ce champ`}
                                    className={`text-[11px] px-2 py-0.5 rounded-md ${TRUST_STYLE[f.niveau] || "bg-slate-100 text-slate-500"}`}>
                                    {f.label} · {f.niveau}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Vue inverse : par information cherchée */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-heading font-semibold text-slate-800">Où chaque information est cherchée</h3>
                  <p className="text-sm text-slate-500 mt-1 mb-4">
                    Une information absente de toutes ces sources reste vide : le moteur
                    n’écrit jamais une valeur qu’aucune source ne prouve.
                  </p>
                  <div className="divide-y divide-slate-100">
                    {catalog.fields.map((f) => (
                      <div key={f.field} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
                        <span className="text-sm font-medium text-slate-700 min-w-[150px]">{f.label}</span>
                        <span className="text-xs text-slate-500">{f.sources.join(" · ")}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ce dont l'agent IA dispose */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-heading font-semibold text-slate-800">Recherches supplémentaires de l’IA</h3>
                  <p className="text-sm text-slate-500 mt-1 mb-4">
                    En mode « Avec IA », l’agent peut demander ces recherches — et rien d’autre.
                    Il n’a aucun accès direct à la base ni au web.
                  </p>
                  <div className="divide-y divide-slate-100 mb-4">
                    {catalog.ai.tools.map((t) => (
                      <div key={t.tool} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
                        <span className="text-xs font-mono text-slate-500 min-w-[170px]">{t.tool}</span>
                        <span className="text-sm text-slate-600">{t.utilite}</span>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-xs font-medium text-slate-600 mb-2">Budgets proposés au lancement</div>
                    <div className="space-y-1">
                      {catalog.ai.budgets.map((b) => (
                        <div key={b.id} className="text-xs text-slate-500">
                          <span className="font-medium text-slate-600 capitalize">{b.id}</span> — jusqu’à {b.provider_calls} interrogations
                          de sources, {b.web_requests} recherches supplémentaires, {b.iterations} passe{b.iterations > 1 ? "s" : ""} d’analyse,
                          {" "}{b.duration_seconds} s maximum.
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">Version du prompt : {catalog.ai.prompt_version}</p>
                  </div>
                </div>
              </>
            ) : <Loader label="Chargement du catalogue des sources…" />
          )}

          {/* Intégrations — clés IA (Gemini) & Email (Resend) */}
          {tab === "integrations" && (
            integrations ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-heading font-semibold text-slate-800">Intégrations</h3>
                <p className="text-sm text-slate-500 mt-1 mb-5">
                  Clés utilisées par les fonctionnalités IA et l’envoi d’emails réel.
                  {!isAdmin && " Lecture seule — réservé aux administrateurs."}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Gemini */}
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                      <Sparkles size={15} className="text-indigo-500" /> Intelligence artificielle (Gemini)
                    </div>
                    <label className="text-xs text-slate-500">Clé API Gemini</label>
                    <input type="password" data-testid="gemini-api-key" disabled={!isAdmin}
                      value={integrations.gemini_api_key}
                      onChange={(e) => { setIntegrations((s) => ({ ...s, gemini_api_key: e.target.value })); touch("integrations"); }}
                      onFocus={(e) => { if (e.target.value === SECRET_MASK) setIntegrations((s) => ({ ...s, gemini_api_key: "" })); }}
                      placeholder="AIza…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono disabled:bg-slate-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" />
                    <label className="text-xs text-slate-500 mt-3 block">Modèle</label>
                    {/* Liste servie par le backend, interrogée sur la clé elle-même
                        (crm/gemini_client.list_models) : Google ferme des modèles aux
                        nouveaux comptes, et un choix figé ne se voyait qu'au premier
                        appel IA, en 404. */}
                    <select data-testid="gemini-model" disabled={!isAdmin}
                      value={integrations.gemini_model}
                      onChange={(e) => { setIntegrations((s) => ({ ...s, gemini_model: e.target.value })); touch("integrations"); }}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white disabled:bg-slate-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none">
                      <option value="">Modèle par défaut ({integrations.gemini_model_default})</option>
                      {(integrations.gemini_models || []).map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                      {/* Un modèle enregistré hors liste (config antérieure) reste
                          sélectionné : sans cette option le select afficherait
                          autre chose que ce qui tourne réellement. */}
                      {integrations.gemini_model
                        && !(integrations.gemini_models || []).some((m) => m.id === integrations.gemini_model) && (
                        <option value={integrations.gemini_model}>{integrations.gemini_model} (hors liste)</option>
                      )}
                    </select>
                    {/* Le modèle enregistré n'est plus servi par cette clé : le dire ici
                        plutôt que de laisser l'erreur 404 tomber au premier appel IA. */}
                    {integrations.gemini_models_source === "api" && integrations.gemini_model
                      && !(integrations.gemini_models || []).some((m) => m.id === integrations.gemini_model) && (
                      <p data-testid="gemini-model-retired" className="mt-1.5 text-xs text-amber-600">
                        Ce modèle n’est plus proposé par votre clé — choisissez-en un dans la liste.
                      </p>
                    )}
                    {integrations.gemini_models_source === "fallback" && (
                      <p className="mt-1.5 text-xs text-slate-400">
                        Liste par défaut — enregistrez une clé valide pour voir les modèles réellement ouverts à votre compte.
                      </p>
                    )}
                    {isAdmin && (
                      <button data-testid="test-gemini" onClick={() => testIntegration("gemini")} disabled={testing === "gemini"}
                        className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-60">
                        <RefreshCw size={13} className={testing === "gemini" ? "animate-spin" : ""} /> Tester la clé
                      </button>
                    )}
                    {testResults.gemini && (
                      <div className={`mt-2 flex items-start gap-1.5 text-xs ${testResults.gemini.ok ? "text-emerald-600" : "text-rose-600"}`}>
                        {testResults.gemini.ok ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <XCircle size={14} className="shrink-0 mt-0.5" />}
                        <span>{testResults.gemini.ok ? `Réponse : ${testResults.gemini.sample}` : testResults.gemini.error}</span>
                      </div>
                    )}
                  </div>

                  {/* Resend */}
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                      <Mail size={15} className="text-indigo-500" /> Email (Resend)
                    </div>
                    <label className="text-xs text-slate-500">Clé API Resend</label>
                    <input type="password" data-testid="resend-api-key" disabled={!isAdmin}
                      value={integrations.resend_api_key}
                      onChange={(e) => { setIntegrations((s) => ({ ...s, resend_api_key: e.target.value })); touch("integrations"); }}
                      onFocus={(e) => { if (e.target.value === SECRET_MASK) setIntegrations((s) => ({ ...s, resend_api_key: "" })); }}
                      placeholder="re_…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono disabled:bg-slate-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" />
                    <label className="text-xs text-slate-500 mt-3 block">Adresse d’expédition</label>
                    <input data-testid="email-from" disabled={!isAdmin}
                      value={integrations.email_from}
                      onChange={(e) => { setIntegrations((s) => ({ ...s, email_from: e.target.value })); touch("integrations"); }}
                      placeholder="ProspectLab <no-reply@votredomaine.fr>" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm disabled:bg-slate-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" />
                    {isAdmin && (
                      <button data-testid="test-resend" onClick={() => testIntegration("resend")} disabled={testing === "resend"}
                        className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-60">
                        <RefreshCw size={13} className={testing === "resend" ? "animate-spin" : ""} /> Tester la clé
                      </button>
                    )}
                    {testResults.resend && (
                      <div className={`mt-2 flex items-start gap-1.5 text-xs ${testResults.resend.ok ? "text-emerald-600" : "text-rose-600"}`}>
                        {testResults.resend.ok ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <XCircle size={14} className="shrink-0 mt-0.5" />}
                        <span>{testResults.resend.ok ? "Connexion Resend OK" : testResults.resend.error}</span>
                      </div>
                    )}
                  </div>

                  {/* Google Places — fiche Google Business pour l'enrichissement */}
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                      <MapPin size={15} className="text-indigo-500" /> Fiche Google (Places API)
                    </div>
                    <label className="text-xs text-slate-500">Clé API Google Places</label>
                    <input type="password" data-testid="places-api-key" disabled={!isAdmin}
                      value={integrations.google_places_api_key}
                      onChange={(e) => { setIntegrations((s) => ({ ...s, google_places_api_key: e.target.value })); touch("integrations"); }}
                      onFocus={(e) => { if (e.target.value === SECRET_MASK) setIntegrations((s) => ({ ...s, google_places_api_key: "" })); }}
                      placeholder="AIza…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono disabled:bg-slate-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" />
                    <p className="mt-2 text-xs text-slate-400">
                      Une clé pour deux usages : l'enrichissement lit la fiche Google Business
                      d'une entreprise (téléphone, site web), et les campagnes d'extraction
                      « Google Places » recherchent les établissements d'un type donné par code
                      postal. Clé Google Cloud avec l'API « Places (New) » activée ; chaque
                      recherche est facturée par Google au-delà du quota gratuit.
                    </p>
                    {integrations.google_places_platform_key && (
                      <p className="mt-1.5 text-xs text-emerald-600" data-testid="places-platform-key-hint">
                        Une clé Google Cloud est fournie par la plateforme : ce champ est facultatif.
                        Renseignez-le seulement pour facturer les recherches sur votre propre projet Google.
                      </p>
                    )}
                    {isAdmin && (
                      <button data-testid="test-places" onClick={() => testIntegration("places")} disabled={testing === "places"}
                        className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-60">
                        <RefreshCw size={13} className={testing === "places" ? "animate-spin" : ""} /> Tester la clé
                      </button>
                    )}
                    {testResults.places && (
                      <div className={`mt-2 flex items-start gap-1.5 text-xs ${testResults.places.ok ? "text-emerald-600" : "text-rose-600"}`}>
                        {testResults.places.ok ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <XCircle size={14} className="shrink-0 mt-0.5" />}
                        <span>{testResults.places.ok ? `Résultat : ${testResults.places.sample}` : testResults.places.error}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : <Loader label="Chargement des intégrations…" />
          )}

          {/* Barre d'enregistrement — collante en bas de la colonne de contenu,
              donc jamais décalée par la largeur de la barre latérale.
              bottom-16 et non bottom-4 : le sticky se cale sur le bas de la
              fenêtre, où la barre du softphone (fixed bottom-0, ~41 px, z-40)
              passait devant et masquait les boutons Annuler / Enregistrer.
              64 px la dégagent avec une marge, en restant dans le pb-24 que
              <main> réserve déjà pour cette barre. */}
          {!READONLY_TABS.includes(tab) && (
          <div className="sticky bottom-16 z-20 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-lg">
            <span className="flex-1 text-sm text-slate-500">
              {isDirty ? "Vos changements ne sont pas encore enregistrés." : "Aucune modification en attente."}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => (tab === "integrations" ? loadIntegrations() : load())}
                disabled={!isDirty || busy}
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                data-testid={tab === "integrations" ? "save-integrations" : `save-${tab}`}
                onClick={save}
                disabled={!canSave || busy}
                className="flex-1 sm:flex-none px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40"
              >
                {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Confirmation du recalcul global. `dismissible` est coupé pendant le
          recalcul : ni Échap ni clic sur le fond ne ferment une opération
          déjà lancée. */}
      {rescoreOpen && (
        <Modal onClose={() => setRescoreOpen(false)} dismissible={!rescoring}
          labelledBy="rescore-title" testid="rescore-modal">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-50 flex items-center justify-center">
              <RefreshCw size={18} className={`text-indigo-600 ${rescoring ? "animate-spin" : ""}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="rescore-title" className="font-heading font-semibold text-slate-800">Recalculer tous les scores</h3>
              <p className="text-sm text-slate-500 mt-1.5">
                Le barème enregistré sera appliqué à toutes les entreprises. L’opération remplace les scores existants et peut prendre quelques minutes.
              </p>
            </div>
            <button onClick={() => !rescoring && setRescoreOpen(false)} aria-label="Fermer" className="shrink-0 text-slate-300 hover:text-slate-600">
              <X size={16} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 my-5 text-sm text-slate-600">
            <span>Score maximum du barème</span>
            <span className="font-mono font-medium">{total} pts</span>
          </div>
          {dirty.scoring && (
            <p className="text-xs text-amber-600 mb-4">Enregistrez d’abord vos modifications : le recalcul utilise le barème côté serveur.</p>
          )}
          <div className="flex justify-end gap-2 flex-wrap">
            <button onClick={() => setRescoreOpen(false)} disabled={rescoring}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              Annuler
            </button>
            <button data-testid="rescore-confirm" onClick={rescoreAll} disabled={rescoring}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
              {rescoring ? "Recalcul en cours…" : "Lancer le recalcul"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const cnTab = (active) =>
  `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
    active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`;
